import CheckIcon from '@mui/icons-material/Check'
import {Box, Typography, List, ListItem, ListItemButton, ListItemText} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {Quote} from './useQuote'

const formatValidityDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-')
  return `${day}/${month}/${year}`
}

interface QuoteListProps {
  /** List of quotes to display */
  quotes: Quote[]
  /** Currently selected quote number */
  selectedQuoteNumber: string | undefined
  /** Callback when a quote is selected */
  onSelectQuote: (quoteNumber: string) => void
}

/**
 * Component that displays a scrollable list of quotes with selection functionality.
 * Follows the same design pattern as MaterialList with CheckIcon for selected items.
 *
 * Each quote displays:
 * - Quote name (primary text)
 * - Contract number (secondary text)
 */
export const QuoteList: React.FC<QuoteListProps> = ({
  quotes,
  selectedQuoteNumber,
  onSelectQuote
}) => {
  const {t} = useTranslation()

  return (
    <Box
      sx={{
        maxHeight: '300px',
        overflowY: 'auto',
        width: '100%',
        '&::-webkit-scrollbar': {
          width: '8px'
        }
      }}
    >
      <List
        sx={{
          padding: 0,
          width: '100%',
          bgcolor: 'background.paper'
        }}
        data-test-id="rmc-quote-selection"
      >
        {quotes.length > 0 ? (
          quotes.map((quote, index) => (
            <ListItem
              key={quote.quoteNumber}
              sx={{
                padding: 0,
                width: '100%'
              }}
            >
              <ListItemButton
                onClick={() => onSelectQuote(quote.quoteNumber)}
                selected={selectedQuoteNumber === quote.quoteNumber}
                sx={{
                  backgroundColor:
                    selectedQuoteNumber === quote.quoteNumber ? '#f2f8fd' : '#ffffff',
                  borderBottom: index === quotes.length - 1 ? 'none' : '1px solid #e8ecf0',
                  padding: '12px 16px',
                  minHeight: '64px',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#f2f8fd'
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#f2f8fd',
                    '&:hover': {
                      backgroundColor: '#f2f8fd'
                    }
                  }
                }}
                data-test-id={`rmc-quote-option-${quote.quoteNumber}`}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#29333d',
                        textAlign: 'left'
                      }}
                    >
                      {quote.quoteName}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#52667a',
                          textAlign: 'left',
                          display: 'block'
                        }}
                      >
                        {quote.quoteNumber}
                      </Typography>
                      {quote.contractStartDate && quote.contractEndDate && (
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#52667a',
                            textAlign: 'left',
                            display: 'block'
                          }}
                        >
                          {t('orderIntake.rmc.steps.quote.validityRange', {
                            from: formatValidityDate(quote.contractStartDate),
                            to: formatValidityDate(quote.contractEndDate)
                          })}
                        </Typography>
                      )}
                    </>
                  }
                  sx={{
                    margin: 0,
                    flex: 1
                  }}
                />
                {selectedQuoteNumber === quote.quoteNumber && (
                  <CheckIcon
                    sx={{
                      color: '#016ad4',
                      fontSize: '20px',
                      marginLeft: '16px'
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))
        ) : (
          <Box sx={{p: 3, textAlign: 'center'}}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#52667a'
              }}
            >
              {t(
                'orderIntake.rmc.steps.quote.noMatchingQuotes',
                'No quotes match your search. Try different keywords.'
              )}
            </Typography>
          </Box>
        )}
      </List>
    </Box>
  )
}
